import { useState, useCallback, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import { Template } from '@templates/types'

type TemplatesByFolderReturn = Record<string, Template[]>

/**
 * Organize templates by folder name.
 * @param {TemplateType[]} templates List of templates to organize.
 * @returns {TemplatesByFolderReturn}
 */
function organizeTemplatesByFolder(templates: Template[] = []): TemplatesByFolderReturn {
  if (!templates.length) return {}
  return templates.reduce((memo, temp) => {
    if (!memo[temp.folder_id]) {
      memo[temp.folder_id] = []
    }
    memo[temp.folder_id].push(temp)
    return memo
  }, {})
}

interface ToolbarHookReturn {
  filteredTemplatesBySearch: TemplatesByFolderReturn
  onChangeFilter: (e: any) => void
}

interface ToolbarArgs {
  templates: Template[] | undefined
}

/**
 * Toolbar hook that manages the Templates page.
 * @param args
 * @param {TemplateType[]} args.templates The templates displayed.
 * @returns {ToolbarHookReturn}
 */
export const useToolbar = ({ templates = [] }: ToolbarArgs): ToolbarHookReturn => {
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>(templates)
  // Search through templates and filter based on term.
  const onChangeFilter = useCallback(
    debounce(e => {
      const { value } = e.target

      if (!value) {
        setFilteredTemplates(templates)
        return
      }

      if (value.length < 3) return // After !value is necessary.

      const filtered = templates?.filter(tmpl => {
        if (tmpl.name?.toLowerCase().includes(value.toLowerCase())) {
          return true
        }

        if (tmpl.description?.toLowerCase().includes(value.toLowerCase())) {
          return true
        }

        return false
      })

      setFilteredTemplates(filtered)
    }, 250),
    [templates],
  )

  useEffect(() => {
    if (!templates?.length) return
    setFilteredTemplates(templates)
  }, [templates])

  const filteredTemplatesBySearch = useMemo(
    () => organizeTemplatesByFolder(filteredTemplates),
    [filteredTemplates],
  )

  return {
    filteredTemplatesBySearch,
    onChangeFilter,
  }
}
